import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';
import { api, apiChannelAPI, type ApiChannel, type ModelConfigData, type ModelRoutingConfigData } from '../../services/api';
import toast from '../Toast';

type RoutingMatrix = NonNullable<ModelRoutingConfigData['routingOverrides']>;
type RoutingMode = keyof RoutingMatrix;
type RoutingQuality = keyof RoutingMatrix['text2img'];

interface ModelRoutingFormProps {
  model: ModelConfigData;
  onClose: () => void;
  onSuccess: () => void;
}

const EMPTY_MATRIX: RoutingMatrix = {
  text2img: { low: null, medium: null, high: null },
  img2img: { low: null, medium: null, high: null },
  inpaint: { low: null, medium: null, high: null },
};

const MODE_LABELS = {
  text2img: '文生图',
  img2img: '图生图',
  inpaint: '局部重绘',
} as const;

const QUALITY_LABELS = {
  low: '标准(1K)',
  medium: '高清(2K)',
  high: '超清(4K)',
} as const;

const ROUTING_MODES: RoutingMode[] = ['text2img', 'img2img', 'inpaint'];
const ROUTING_QUALITIES: RoutingQuality[] = ['low', 'medium', 'high'];
const CREDIT_YUAN = 0.05;

function formatYuan(value: number, digits = 3): string {
  return `¥${value.toFixed(digits)}`;
}

function formatSignedYuan(value: number, digits = 3): string {
  return `${value >= 0 ? '+' : '-'}${formatYuan(Math.abs(value), digits)}`;
}

function formatPercent(value: number, digits = 1): string {
  return `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(digits)}%`;
}

function cloneMatrix(matrix?: Partial<RoutingMatrix> | null): RoutingMatrix {
  return {
    text2img: { ...EMPTY_MATRIX.text2img, ...(matrix?.text2img || {}) },
    img2img: { ...EMPTY_MATRIX.img2img, ...(matrix?.img2img || {}) },
    inpaint: { ...EMPTY_MATRIX.inpaint, ...(matrix?.inpaint || {}) },
  };
}

// 可排序的降级渠道行
function SortableFallbackRow({
  channelId,
  index,
  channelOptions,
  onRemove,
  onChange,
}: {
  channelId: string;
  index: number;
  channelOptions: { id: string; label: string }[];
  onRemove: () => void;
  onChange: (newId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: channelId + '-' + index });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex min-w-0 items-center gap-2 rounded-xl border border-light-border bg-light-hover/30 px-3 py-2">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab text-light-muted hover:text-light-text active:cursor-grabbing"
        title="拖拽排序"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-blue-50 px-1.5 text-[11px] font-medium text-accent-blue">
        L{index + 3}
      </span>
      <select
        value={channelId}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 rounded-lg border border-light-border bg-white px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue focus:ring-2 focus:ring-blue-50"
      >
        {channelOptions.map((opt) => (
          <option key={opt.id} value={opt.id}>{opt.label}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 rounded-lg p-1 text-light-muted transition-colors hover:bg-rose-50 hover:text-rose-600"
        title="移除"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function ModelRoutingForm({ model, onClose, onSuccess }: ModelRoutingFormProps) {
  const [channels, setChannels] = useState<ApiChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState<RoutingMode>('text2img');
  const [previewQuality, setPreviewQuality] = useState<RoutingQuality>('medium');
  const [fallbackChannelIds, setFallbackChannelIds] = useState<string[]>([]);
  const [form, setForm] = useState<ModelRoutingConfigData>({
    id: model.id,
    modelId: model.modelId,
    name: model.name,
    fallbackChannelIds: model.fallbackChannelIds || [],
    text2imgChannelId: model.text2imgChannelId || null,
    img2imgChannelId: model.img2imgChannelId || null,
    inpaintChannelId: model.inpaintChannelId || null,
    enableFallbackChain: model.enableFallbackChain ?? false,
    routingOverrides: cloneMatrix(model.routingOverrides),
    routingSummary: model.routingSummary || { defaultRuleCount: 0, overrideCount: 0 },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [channelsRes, routingRes] = await Promise.all([
          apiChannelAPI.getChannels(),
          api.getModelRouting(model.id),
        ]);
        if (cancelled) return;
        setChannels(channelsRes.data.filter((channel) => channel.isActive));
        const loadedFallback = routingRes.data.fallbackChannelIds || [];
        setFallbackChannelIds(loadedFallback);
        setForm({
          ...routingRes.data,
          fallbackChannelIds: loadedFallback,
          routingOverrides: cloneMatrix(routingRes.data.routingOverrides),
        });
      } catch (error: any) {
        if (!cancelled) toast.error(error.message || '获取路由配置失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [model.id]);

  const channelOptions = useMemo(
    () => channels.map((channel) => ({
      id: channel.id,
      label: `${channel.name} | ${channel.apiModelId || '未配置modelId'}`,
    })),
    [channels],
  );
  const inputClass = 'w-full rounded-xl border border-light-border bg-white px-4 py-3 text-light-text outline-none transition focus:border-accent-blue focus:ring-4 focus:ring-blue-50';
  const cardClass = 'min-w-0 rounded-2xl border border-light-border bg-white p-4 shadow-sm sm:p-5';
  const modeEnabled = useMemo<Record<RoutingMode, boolean>>(
    () => ({
      text2img: model.enableText2img !== false,
      img2img: model.enableImg2img !== false,
      inpaint: model.enableInpaint !== false,
    }),
    [model.enableImg2img, model.enableInpaint, model.enableText2img],
  );
  const qualityEnabled = useMemo<Record<RoutingQuality, boolean>>(
    () => ({
      low: model.enableStandardSize !== false,
      medium: model.enableHdSize !== false,
      high: model.enableUhdSize !== false,
    }),
    [model.enableHdSize, model.enableStandardSize, model.enableUhdSize],
  );

  const getModeDisabledReason = (mode: RoutingMode) => (
    modeEnabled[mode] ? null : `当前模型未启用${MODE_LABELS[mode]}能力`
  );
  const getQualityDisabledReason = (quality: RoutingQuality) => (
    qualityEnabled[quality] ? null : `当前模型未启用${QUALITY_LABELS[quality]}`
  );
  const getCellDisabledReason = (mode: RoutingMode, quality: RoutingQuality) => (
    getModeDisabledReason(mode) || getQualityDisabledReason(quality)
  );
  const enabledPreviewModes = ROUTING_MODES.filter((mode) => modeEnabled[mode]);
  const enabledPreviewQualities = ROUTING_QUALITIES.filter((quality) => qualityEnabled[quality]);

  const setOverride = (mode: RoutingMode, quality: RoutingQuality, value: string) => {
    setForm((prev) => ({
      ...prev,
      routingOverrides: {
        ...prev.routingOverrides,
        [mode]: {
          ...prev.routingOverrides[mode],
          [quality]: value || null,
        },
      },
    }));
  };

  const findChannel = (channelId: string | null | undefined) => channels.find((channel) => channel.id === channelId) || null;
  const formatChannelLabel = (channelId: string | null | undefined, fallbackText = '未配置可用渠道') => {
    const channel = findChannel(channelId);
    if (!channel) return fallbackText;
    return `${channel.name} | ${channel.apiModelId || '未配置modelId'}`;
  };
  const getChannelCost = (channel: ApiChannel | null, quality: RoutingQuality): number | null => {
    if (!channel) return null;
    const cost = quality === 'low'
      ? channel.costPerCallLow
      : quality === 'medium'
        ? channel.costPerCallMedium
        : channel.costPerCallHigh;
    return Number.isFinite(cost) ? cost : null;
  };
  const getQualityCreditCost = (quality: RoutingQuality) => {
    if (quality === 'low') return model.standardCredits;
    if (quality === 'medium') return model.hdCredits;
    return model.uhdCredits;
  };
  const getModeCreditCost = (mode: RoutingMode) => {
    if (mode === 'text2img') return model.text2imgCredits;
    if (mode === 'img2img') return model.img2imgCredits;
    return model.inpaintCredits;
  };
  const previewCreditCost = getQualityCreditCost(previewQuality) + getModeCreditCost(previewMode);
  const previewRevenue = previewCreditCost * CREDIT_YUAN;
  const ATTEMPT_CHIP_CLASSNAMES = [
    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    'bg-sky-50 text-sky-700 ring-1 ring-sky-200',
    'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  ] as const;

  const getAttemptChipClassName = (attemptIndex: number) => (
    ATTEMPT_CHIP_CLASSNAMES[Math.min(attemptIndex, ATTEMPT_CHIP_CLASSNAMES.length - 1)]
  );

  const renderChannelChip = (
    channelId: string | null | undefined,
    attemptIndex: number,
    fallbackText = '未配置可用渠道',
  ) => {
    const channel = findChannel(channelId);
    const chipClassName = getAttemptChipClassName(attemptIndex);
    if (!channel) {
      return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${chipClassName}`}>
          {fallbackText}
        </span>
      );
    }

    const cost = getChannelCost(channel, previewQuality);
    const margin = cost === null ? null : previewRevenue - cost;
    const marginPercent = margin === null || previewRevenue <= 0 ? null : (margin / previewRevenue) * 100;
    const costBadgeClassName = marginPercent === null
      ? 'bg-slate-100 text-slate-600'
      : marginPercent < 0
        ? 'bg-rose-100 text-rose-700'
        : marginPercent <= 20
          ? 'bg-amber-100 text-amber-700'
          : 'bg-emerald-100 text-emerald-700';
    const title = `${formatChannelLabel(channel.id)} | 成本 ${cost === null ? '未配置' : formatYuan(cost)}${margin === null ? '' : ` | 毛利 ${formatSignedYuan(margin)}`}${marginPercent === null ? '' : ` | 毛利率 ${formatPercent(marginPercent)}`}`;

    return (
      <span
        className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${chipClassName}`}
        title={title}
      >
        <span className="max-w-[110px] truncate sm:max-w-[220px]">{channel.name}</span>
        <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] ${costBadgeClassName}`}>
          {cost === null ? '未配置成本' : `${formatYuan(cost)} · ${marginPercent === null ? '—' : formatPercent(marginPercent)}`}
        </span>
      </span>
    );
  };

  // dnd-kit 拖拽排序回调
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFallbackChannelIds((prev) => {
      const oldIndex = prev.findIndex((_, i) => active.id === prev[i] + '-' + i);
      const newIndex = prev.findIndex((_, i) => over.id === prev[i] + '-' + i);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const addFallbackChannel = () => {
    const firstAvailable = channels[0];
    if (firstAvailable) {
      setFallbackChannelIds((prev) => [...prev, firstAvailable.id]);
    }
  };

  const removeFallbackChannel = (index: number) => {
    setFallbackChannelIds((prev) => prev.filter((_, i) => i !== index));
  };

  const changeFallbackChannel = (index: number, newId: string) => {
    setFallbackChannelIds((prev) => prev.map((id, i) => (i === index ? newId : id)));
  };

  useEffect(() => {
    if (!modeEnabled[previewMode] && enabledPreviewModes.length > 0) {
      setPreviewMode(enabledPreviewModes[0]);
    }
  }, [enabledPreviewModes, modeEnabled, previewMode]);

  useEffect(() => {
    if (!qualityEnabled[previewQuality] && enabledPreviewQualities.length > 0) {
      setPreviewQuality(enabledPreviewQualities[0]);
    }
  }, [enabledPreviewQualities, previewQuality, qualityEnabled]);

  const previewUnavailableReason = getCellDisabledReason(previewMode, previewQuality);
  const previewOverrideId = form.routingOverrides[previewMode][previewQuality];
  const previewModeDefaultId = form[`${previewMode}ChannelId` as 'text2imgChannelId' | 'img2imgChannelId' | 'inpaintChannelId'];
  const previewFallbackIds = fallbackChannelIds;
  const previewFirstFallbackId = previewFallbackIds[0] || null;
  const previewResolvedId = previewOverrideId || previewModeDefaultId || previewFirstFallbackId || null;
  const previewResolvedReason = previewUnavailableReason
    ? `当前组合未启用，${previewUnavailableReason}`
    : previewOverrideId
      ? '命中专属覆盖'
      : previewModeDefaultId
        ? '未配置专属覆盖，命中模式默认'
        : previewFirstFallbackId
          ? '未配置专属覆盖和模式默认，命中降级链首个渠道'
          : '当前组合没有可用渠道';
  const previewAttemptChain = (() => {
    if (previewUnavailableReason) return [] as Array<{ key: string; channelId: string }>;

    const entries: Array<{ key: string; channelId: string }> = [];
    // 首选段去重（与后端 buildImageChannelAttemptChain 一致）
    const seenIds = new Set<string>();
    const pushUnique = (key: string, channelId: string | null | undefined) => {
      if (!channelId) return;
      if (seenIds.has(channelId)) return;
      seenIds.add(channelId);
      entries.push({ key, channelId });
    };

    pushUnique('override', previewOverrideId);
    if (entries.length === 0) pushUnique('mode', previewModeDefaultId);

    if (form.enableFallbackChain && entries.length > 0) {
      // override 命中后，mode 作为第二候选
      if (previewOverrideId && !seenIds.has(previewModeDefaultId || '')) {
        pushUnique('mode-fallback', previewModeDefaultId);
      }
      // fallback 段：与首选段去重，但内部不去重（支持显式写两次重试）
      for (let i = 0; i < previewFallbackIds.length; i++) {
        const cid = previewFallbackIds[i];
        if (!cid) continue;
        if (seenIds.has(cid)) continue; // 与首选段重复则跳过
        entries.push({ key: `fallback-${i}`, channelId: cid });
      }
    } else if (entries.length === 0) {
      // 无 override/mode 时 fallback[0] 作首选（不计入 seenIds，使后续同 id 仍能进链）
      if (previewFallbackIds.length > 0) {
        entries.push({ key: 'fallback-0', channelId: previewFallbackIds[0] });
        if (form.enableFallbackChain) {
          for (let i = 1; i < previewFallbackIds.length; i++) {
            const cid = previewFallbackIds[i];
            if (!cid) continue;
            entries.push({ key: `fallback-${i}`, channelId: cid });
          }
        }
      }
    }

    return entries;
  })();
  const previewCosts = previewAttemptChain
    .map((entry) => getChannelCost(findChannel(entry.channelId), previewQuality))
    .filter((cost): cost is number => cost !== null);
  const previewCostMin = previewCosts.length ? Math.min(...previewCosts) : null;
  const previewCostMax = previewCosts.length ? Math.max(...previewCosts) : null;
  const previewLastEntry = previewAttemptChain[previewAttemptChain.length - 1];
  const previewLastCost = getChannelCost(findChannel(previewLastEntry?.channelId), previewQuality);
  const previewLastMargin = previewLastCost === null ? null : previewRevenue - previewLastCost;
  const previewLastMarginPercent = previewLastMargin === null || previewRevenue <= 0
    ? null
    : (previewLastMargin / previewRevenue) * 100;

  const renderOverrideControl = (mode: RoutingMode, quality: RoutingQuality) => {
    const disabledReason = getCellDisabledReason(mode, quality);
    return (
      <>
        <select
          value={form.routingOverrides[mode][quality] || ''}
          onChange={(e) => setOverride(mode, quality, e.target.value)}
          className={`${inputClass} min-w-0 ${disabledReason ? 'border-red-300 bg-red-50/40 text-red-500 focus:border-red-300 focus:ring-red-50' : ''}`}
          disabled={Boolean(disabledReason)}
        >
          <option value="">继承下级</option>
          {channelOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
        {disabledReason ? (
          <p className="mt-1 text-xs text-amber-600">
            {disabledReason}
            {form.routingOverrides[mode][quality] ? '，当前仅保留历史配置，不生效。' : '，因此不能选择渠道。'}
          </p>
        ) : null}
      </>
    );
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateModelRouting(model.id, {
        fallbackChannelIds,
        text2imgChannelId: form.text2imgChannelId || null,
        img2imgChannelId: form.img2imgChannelId || null,
        inpaintChannelId: form.inpaintChannelId || null,
        enableFallbackChain: form.enableFallbackChain,
        routingOverrides: form.routingOverrides,
      } as any);
      toast.success('路由配置已保存');
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || '保存路由配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 w-full min-w-0 max-h-[calc(92vh-96px)] flex-col sm:max-h-[calc(90vh-120px)]">
      <div className="min-h-0 min-w-0 flex-1 space-y-5 overflow-y-auto pr-1">
        <section className={cardClass}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-light-text">路由概览</h4>
              <p className="mt-1 text-xs leading-5 text-light-muted">优先级固定为：分辨率覆盖（L1），其次模式默认（L2），再次降级兜底链（L3 起可变 N 级）。</p>
            </div>
            <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-medium text-accent-blue sm:text-xs">
              基础链路 {form.routingSummary?.defaultRuleCount ?? 0} 条 / 覆盖 {form.routingSummary?.overrideCount ?? 0} 条
            </span>
          </div>
        </section>

        <section className={cardClass}>
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3">
            <input
              type="checkbox"
              checked={form.enableFallbackChain}
              onChange={(e) => setForm({ ...form, enableFallbackChain: e.target.checked })}
              className="mt-1 h-4 w-4 shrink-0 rounded border-amber-300"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-light-text">开启路由降级</p>
              <p className="mt-1 text-xs leading-5 text-light-muted">开启后，首选渠道（L1/L2）失败时会继续按降级兜底链逐个尝试；关闭后只使用首选命中的渠道。在降级链里写两次同一渠道 id 可显式触发重试。后续渠道的图生图参考图上限不能低于首选渠道。</p>
            </div>
          </div>
        </section>

        <section className={cardClass}>
          <h4 className="text-sm font-semibold text-light-text">高级覆盖（L1）</h4>
          <p className="mt-1 text-xs leading-5 text-light-muted">只在某个模式和分辨率需要单独命中渠道时配置，未选择则自动继承 L2 或降级链。</p>
          <div className="mt-4 hidden overflow-x-auto lg:block">
            <table className="min-w-full border-separate border-spacing-3">
              <thead>
                <tr>
                  <th className="min-w-[120px] text-left text-xs font-medium text-light-muted">模式</th>
                  <th className="min-w-[240px] text-left text-xs font-medium text-light-muted">标准(1K)</th>
                  <th className="min-w-[240px] text-left text-xs font-medium text-light-muted">高清(2K)</th>
                  <th className="min-w-[240px] text-left text-xs font-medium text-light-muted">超清(4K)</th>
                </tr>
              </thead>
              <tbody>
                {ROUTING_MODES.map((mode) => (
                  <tr key={mode}>
                    <td className="rounded-xl border border-light-border bg-light-hover/40 px-3 py-3 text-sm font-medium text-light-text">{MODE_LABELS[mode]}</td>
                    {ROUTING_QUALITIES.map((quality) => (
                      <td key={quality} className="min-w-0">
                        {renderOverrideControl(mode, quality)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 space-y-3 lg:hidden">
            {ROUTING_MODES.map((mode) => (
              <div key={mode} className="rounded-xl border border-light-border bg-light-hover/20 p-3">
                <p className="text-sm font-medium text-light-text">{MODE_LABELS[mode]}</p>
                <div className="mt-3 space-y-3">
                  {ROUTING_QUALITIES.map((quality) => (
                    <div key={quality} className="min-w-0">
                      <label className="mb-1 block text-xs font-medium text-light-muted">{QUALITY_LABELS[quality]}</label>
                      {renderOverrideControl(mode, quality)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={cardClass}>
          <h4 className="text-sm font-semibold text-light-text">基础路由（L2）</h4>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {ROUTING_MODES.map((mode) => (
              <div key={mode}>
                <label className="mb-1.5 block text-sm font-medium text-light-text">{MODE_LABELS[mode]}默认渠道</label>
                <select
                  value={form[`${mode}ChannelId`] || ''}
                  onChange={(e) => setForm({ ...form, [`${mode}ChannelId`]: e.target.value || null })}
                  className={inputClass}
                  disabled={!modeEnabled[mode]}
                >
                  <option value="">继承降级链</option>
                  {channelOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
                {!modeEnabled[mode] ? (
                  <p className="mt-1 text-xs text-amber-600">
                    {getModeDisabledReason(mode)}
                    {form[`${mode}ChannelId`] ? '，当前仅保留历史配置，不生效。' : '，因此不能选择渠道。'}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <section className={cardClass}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-light-text">降级兜底链（L3 起）</h4>
              <p className="mt-1 text-xs leading-5 text-light-muted">L1/L2 都未命中或失败时，按列表顺序逐个尝试。可在同一渠道写两次实现显式重试。拖拽手柄排序。</p>
            </div>
            <button
              type="button"
              onClick={addFallbackChannel}
              disabled={channels.length === 0}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-light-border bg-white px-3 py-1.5 text-xs font-medium text-light-text transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              添加渠道
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {fallbackChannelIds.length === 0 ? (
              <div className="rounded-xl border border-dashed border-light-border bg-light-hover/20 px-4 py-6 text-center text-xs text-light-muted">
                尚未配置降级兜底渠道。点击"添加渠道"开始构建降级链。
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fallbackChannelIds.map((id, i) => id + '-' + i)}
                  strategy={verticalListSortingStrategy}
                >
                  {fallbackChannelIds.map((channelId, index) => (
                    <SortableFallbackRow
                      key={channelId + '-' + index}
                      channelId={channelId}
                      index={index}
                      channelOptions={channelOptions}
                      onRemove={() => removeFallbackChannel(index)}
                      onChange={(newId) => changeFallbackChannel(index, newId)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </section>

        <section className={cardClass}>
          <h4 className="text-sm font-semibold text-light-text">命中预览</h4>
          <p className="mt-1 text-xs leading-5 text-light-muted">选择一个组合，实时查看它会按什么顺序命中渠道。</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-light-text">模式</label>
              <select value={previewMode} onChange={(e) => setPreviewMode(e.target.value as RoutingMode)} className={inputClass}>
                {ROUTING_MODES.map((mode) => (
                  <option key={mode} value={mode} disabled={!modeEnabled[mode]}>{MODE_LABELS[mode]}</option>
                ))}
              </select>
              {!modeEnabled[previewMode] ? (
                <p className="mt-1 text-xs text-amber-600">{getModeDisabledReason(previewMode)}</p>
              ) : null}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-light-text">分辨率</label>
              <select value={previewQuality} onChange={(e) => setPreviewQuality(e.target.value as RoutingQuality)} className={inputClass}>
                {ROUTING_QUALITIES.map((quality) => (
                  <option key={quality} value={quality} disabled={!qualityEnabled[quality]}>{QUALITY_LABELS[quality]}</option>
                ))}
              </select>
              {!qualityEnabled[previewQuality] ? (
                <p className="mt-1 text-xs text-amber-600">{getQualityDisabledReason(previewQuality)}</p>
              ) : null}
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-light-border bg-light-hover/30 px-4 py-3">
            <p className="text-sm font-medium text-light-text">
              当前请求：{MODE_LABELS[previewMode]} + {QUALITY_LABELS[previewQuality]}
            </p>
            <p className="mt-2 text-xs text-light-muted">
              最终结果：{previewResolvedId ? `${formatChannelLabel(previewResolvedId)} -> ${previewResolvedReason}` : previewResolvedReason}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-light-muted">
              <span>失败回退：</span>
              {(() => {
                if (previewUnavailableReason) {
                  return <span>{previewResolvedReason}</span>;
                }
                if (!previewAttemptChain.length) {
                  return <span>当前组合没有可用尝试链</span>;
                }

                return (
                  <>
                    {previewAttemptChain.map((entry, index) => (
                      <Fragment key={entry.key}>
                        {index > 0 ? <span className="text-light-muted">-&gt;</span> : null}
                        {renderChannelChip(entry.channelId, index)}
                      </Fragment>
                    ))}
                    <span className="text-light-muted">-&gt;</span>
                    <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 ring-1 ring-rose-200">
                      失败
                    </span>
                  </>
                );
              })()}
            </div>
            {!previewUnavailableReason && previewAttemptChain.length > 0 ? (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-light-border pt-2 text-[11px] text-light-muted">
                <span>
                  收入 <span className="font-mono text-light-text">{previewCreditCost} 积分 ≈ {formatYuan(previewRevenue, 2)}</span>
                </span>
                {previewCostMin !== null && previewCostMax !== null ? (
                  <span>
                    成本区间 <span className="font-mono text-light-text">{formatYuan(previewCostMin)} – {formatYuan(previewCostMax)}</span>
                  </span>
                ) : (
                  <span>成本未配置</span>
                )}
                {previewLastMargin !== null ? (
                  <span className={previewLastMarginPercent === null ? 'text-light-muted' : previewLastMarginPercent < 0 ? 'text-rose-600' : previewLastMarginPercent <= 20 ? 'text-amber-600' : 'text-emerald-600'}>
                    末级毛利 <span className="font-mono">{formatSignedYuan(previewLastMargin)}</span>
                    {previewLastMarginPercent !== null ? <span className="font-mono">（毛利率 {formatPercent(previewLastMarginPercent)}）</span> : null}
                  </span>
                ) : null}
                {!form.enableFallbackChain ? <span>未开启降级链，仅显示首选</span> : null}
                <span>按 1 积分 = ¥0.05 估算</span>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <div className="mt-4 flex shrink-0 flex-col gap-3 border-t border-light-border bg-light-bg pt-3 sm:mt-5 sm:flex-row sm:items-center sm:justify-between sm:pt-4">
        <p className="text-[11px] text-light-muted sm:text-xs">{loading ? '正在加载最新路由配置...' : '保存后新任务会立即按新的路由规则命中渠道。'}</p>
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <button type="button" onClick={onClose} className="flex-1 rounded-xl px-4 py-2 text-light-muted transition-colors hover:bg-light-hover hover:text-light-text sm:flex-none">
            取消
          </button>
          <button type="submit" disabled={loading || saving} className="flex-1 rounded-xl bg-accent-blue px-6 py-2 font-medium text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none">
            {saving ? '保存中...' : '保存路由'}
          </button>
        </div>
      </div>
    </form>
  );
}
